import type { SharingInviteEntry, WorldDoc } from '@mod/core/model'

export type SharingInvite = [string, SharingInviteEntry]

function normalizedEmail(email: string): string {
  return email.trim().toLowerCase()
}

function isActive(invite: SharingInviteEntry, now: number): boolean {
  return (
    invite.status === 'joined' ||
    (invite.status === 'pending' && invite.expiresAt >= now)
  )
}

function preferred(
  current: SharingInviteEntry,
  candidate: SharingInviteEntry,
): boolean {
  if (current.status !== candidate.status) return candidate.status === 'joined'
  return candidate.createdAt > current.createdAt
}

/** One active row per person and target. Joined access wins over a later
 * redundant pending invite so old duplicate records do not leak into the UI.
 */
export function uniqueActiveInvites(
  invites: WorldDoc['sharingInvites'],
  documentId: string,
  now = Date.now(),
): SharingInvite[] {
  const byEmail = new Map<string, SharingInvite>()
  for (const entry of Object.entries(invites ?? {})) {
    const [, invite] = entry
    if (invite.target.documentId !== documentId || !isActive(invite, now))
      continue
    const email = normalizedEmail(invite.email)
    const existing = byEmail.get(email)
    if (!existing || preferred(existing[1], invite)) byEmail.set(email, entry)
  }
  return [...byEmail.values()].sort(([, a], [, b]) => a.createdAt - b.createdAt)
}

export function inviteForEmail(
  invites: WorldDoc['sharingInvites'],
  documentId: string,
  email: string,
  now = Date.now(),
): SharingInviteEntry | undefined {
  const wanted = normalizedEmail(email)
  return uniqueActiveInvites(invites, documentId, now).find(
    ([, invite]) => normalizedEmail(invite.email) === wanted,
  )?.[1]
}

/** Documents this person still holds through OTHER joined invites. Removing
 * one page must not take an embed away when another page, or a direct invite
 * to that document, still names it. The caller supplies the person id from
 * signed membership, not an email or display name. */
export function retainedDocumentIds(
  invites: WorldDoc['sharingInvites'],
  personId: string,
  removingDocumentId: string,
): Set<string> {
  const keep = new Set<string>()
  for (const invite of Object.values(invites ?? {})) {
    if (
      invite.status !== 'joined' ||
      invite.target.documentId === removingDocumentId ||
      !invite.joined?.includes(personId)
    )
      continue
    if (invite.target.kind === 'document') keep.add(invite.target.documentId)
    for (const include of invite.target.includes ?? [])
      keep.add(include.documentId)
  }
  return keep
}
